import {
  createRef,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import styles from "./RevenueDetails.module.scss";
import { UnderwritingFormContext } from "../context/UnderwritingFormContext";
import { Field, Form, FormSpy } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import OutlinedFormInput from "../../Finance/components/OutlinedFormInput/OutlinedFormInput";
import {
  findFirstErrorField,
  onBlurFormatNumber,
  parseNumberForDB,
  setFieldTouchedMutator,
} from "../../../components/functions/utilities";
import moment from "moment";
import { getIn } from "final-form";
import {
  UnderwritingSteps,
  uploadStates,
} from "../../../interfaces/interfaces";
import RevenueRow from "./RevenueRow";
import MisReportRow from "./MisReportRow";
import { MdSync, MdCheckBoxOutlineBlank } from "react-icons/md";
import { AiFillCheckSquare } from "react-icons/ai";
import setFieldTouched from "final-form-set-field-touched";
import RevenueError from "./RevenueError";
import { ARRAY_ERROR } from "final-form";
import API from "../../../rest/api";
import { checkIfGridDataIsValid, revenueFormValidation } from "./revenueHelper";
import DebtSchedule from "./DebtSchedule/DebtSchedule";
import { findFirstDebtScheduleGridError } from "./DebtSchedule/debtScheduleHelper";
import DebtorSchedule from "./DebtorSchedule/DebtorSchedule";
import { findFirstDebtorScheduleGridError } from "./DebtorSchedule/debtorScheduleHelper";
import MeritoButton from "../../../components/common/MeritoButtons/MeritoButtons";
// Tally sync is hidden in the underwriting Finance step for now.
// import TallyDashboardSyncModal from "../../../components/common/TallyConnect/TallyDashboardSyncModal/TallyDashboardSyncModal";
// import { useTallyDashboardFlow } from "../../../components/common/TallyConnect/useTallyDashboardFlow";
import { HiArrowNarrowDown as DownArrowIcon } from "react-icons/hi";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import ModalDialog from "../../../components/common/Modal";
import { useZohoConnect } from "../../../components/ZohoConnect/useZohoConnect";
import { useZohoStatus } from "../../../components/ZohoConnect/useZohoStatus";
import { IpRestS } from "../../../rest/ipRest-service";
import { RestS } from "../../../rest/rest-service";
import zohoLogo from "../../../resources/images/gst-recon/zoho.png";
import tallyLogo from "../../../resources/images/gst-recon/tally.png";

export const financialYearLabel = (financialYear) => {
  const financialYears = financialYear.split("-");
  return `${moment(financialYears[0], "YYYY").format("YYYY")}-${moment(
    financialYears[1],
    "YYYY"
  ).format("YY")}`;
};


type Props = {
  setFormPristine: (value: boolean) => void;
  saveToLocalStorage?: (values: Record<string, any>) => void;
};

const RevenueDetails = forwardRef(({ setFormPristine, saveToLocalStorage }: Props, ref) => {
  const {
    formData,
    setFormData,
    setStepValidity,
    formStepsValidity,
    underwritingFileJobs,
    extraFormDetails,
    // isTallyConnected,
    // tallySynced,
    // tallyConnection,
    // fetchTallyConnectionStatus,
  } = useContext(UnderwritingFormContext);
  // console.log('formData', formData)
  const formRef = useRef(null);
  const [deleteFileKeys, setDeleteFileKeys] = useState([]);
  const [newUploadedDocIds, setNewUploadedDocIds] = useState<number[]>([]);
  const [deleteDocIds, setDeleteDocIds] = useState<number[]>([]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const sectionRefs = useRef({
    revenueDetails: createRef(),
    misReports: createRef(),
    debtSchedule: createRef(),
    debtorSchedule: createRef(),
    unsoldInventory: createRef(),
  });
  const sectionRootContainerRef = useRef(null);
  const [currentVisibleSectionIndex, setCurrentVisibleSectionIndex] =
    useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  // const tallyFlow = useTallyDashboardFlow(undefined, undefined, tallyConnection);

  const zohoCompanyId =
    extraFormDetails?.companyId != null ? Number(extraFormDetails.companyId) : undefined;
  const {
    status: zohoStatus,
    accessTokenId: zohoAccessTokenId,
    lastSyncedAt: zohoLastSyncedAt,
    refreshStatus: refreshZohoStatus,
  } = useZohoStatus({ companyId: zohoCompanyId });
  // Books row gating is hidden along with the Books section below - it
  // depends on the Tally connection state that is currently commented out.
  // At most one of Tally/Zoho should be offered as a Books source once the
  // other is connected - if neither is, show both rows and let the user
  // pick.
  // const isTallyBooksConnected = isTallyConnected || tallyFlow.isConnected;
  // const isZohoBooksConnected = zohoStatus !== "not_connected" && zohoStatus !== "checking";
  // isTallyConnected defaults to false before fetchTallyConnectionStatus
  // resolves - indistinguishable from "confirmed not connected", unlike
  // tallyConnection which stays null only until that first fetch
  // completes (the API returns a real object either way, see
  // UnderwritingFormContext's fetchTallyConnectionStatus). Without this,
  // both rows briefly evaluate as "the other one isn't connected yet" and
  // flash together before settling on the real answer.
  // const booksStatusResolved = tallyConnection !== null && zohoStatus !== "checking";
  // Boolean(...) here isn't just style - referencing isZohoBooksConnected
  // directly in JSX lets TS narrow zohoStatus's type from that alias for
  // everything inside the row (since one of its branches, "the other
  // isn't connected", still legitimately allows zohoStatus to be
  // "not_connected"), which then flags the row's own real not_connected
  // checks below as unreachable. Boolean() breaks that false narrowing.
  // const showTallyRow = Boolean(booksStatusResolved && (isTallyBooksConnected || !isZohoBooksConnected));
  // const showZohoRow = Boolean(booksStatusResolved && (isZohoBooksConnected || !isTallyBooksConnected));
  const { connect: connectZoho, isConnecting: isZohoConnecting } = useZohoConnect({
    from: "underwriting-finance",
    companyId: zohoCompanyId,
  });

  const [showZohoModal, setShowZohoModal] = useState(false);
  const [zohoModalView, setZohoModalView] = useState<"connect" | "confirm">("connect");
  const [zohoModalError, setZohoModalError] = useState("");
  const [zohoAccounts, setZohoAccounts] = useState<{ id: string; name: string }[]>([]);
  const [selectedZohoAccountIds, setSelectedZohoAccountIds] = useState<string[]>([]);
  const [isConfirmingZoho, setIsConfirmingZoho] = useState(false);
  const [isZohoResyncing, setIsZohoResyncing] = useState(false);

  const confirmZohoAccounts = (accounts: { id: string; name: string }[]) => {
    setIsConfirmingZoho(true);
    setZohoModalError("");
    IpRestS.getIpByName("Zoho")
      .then((ip) => {
        if (!ip?._id) throw new Error("missing ipId");
        return RestS.confirmZohoConnection(
          ip._id,
          accounts.map((a) => ({ parentId: a.id, parentName: a.name })),
          zohoCompanyId
        );
      })
      .then((res) => {
        if (!res?.success) throw new Error(res?.message || "Could not finish connecting Zoho Books.");
        toast.success("Zoho Books connected");
        setShowZohoModal(false);
        refreshZohoStatus();
      })
      .catch(() => {
        setZohoModalError("Could not finish connecting Zoho Books. Please try again.");
        setZohoModalView("connect");
      })
      .finally(() => setIsConfirmingZoho(false));
  };

  const handleZohoResync = () => {
    if (!zohoAccessTokenId || zohoCompanyId == null) return;
    setIsZohoResyncing(true);
    RestS.zohoOnDemandRefresh(zohoCompanyId, zohoAccessTokenId)
      .then((res) => {
        if (res?.success) {
          toast.success("Zoho Books resync started");
          refreshZohoStatus();
        } else {
          toast.error(res?.message || "Could not start resync. Please try again.");
        }
      })
      .catch(() => toast.error("Could not start resync. Please try again."))
      .finally(() => setIsZohoResyncing(false));
  };

  // useEffect(() => {
  //   // Refresh the form-level connection status once a sync in this session
  //   // completes, so it stays correct (incl. "last synced" month) on future
  //   // mounts without waiting for a full page reload.
  //   if (tallyFlow.stage === "success") {
  //     fetchTallyConnectionStatus?.();
  //   }
  // }, [tallyFlow.stage]);

  useEffect(() => {
    const showError = searchParams.get("error");
    if (showError == "true") {
      formRef?.current?.submit();
      scrollToFirstError();
    }
  }, [searchParams]);

  useEffect(() => {
    // finance-api's Zoho OAuth callback lands the browser back on this page
    // with these query params — pick up the result once, then clear only
    // the Zoho-specific ones (the `error` param above is unrelated and must
    // survive this).
    if (searchParams.get("ipName") !== "Zoho Books") return;
    const success = searchParams.get("success");
    const customersRaw = searchParams.get("customers");
    const next = new URLSearchParams(searchParams);
    next.delete("ipName");
    next.delete("success");
    next.delete("customers");
    setSearchParams(next, { replace: true });

    if (success !== "true") {
      setZohoModalError("Could not connect to Zoho Books. Please try again.");
      setZohoModalView("connect");
      setShowZohoModal(true);
      return;
    }
    try {
      const accounts: { id: string; name: string }[] = JSON.parse(customersRaw ?? "[]");
      if (accounts.length === 0) {
        setZohoModalError("No Zoho organizations were found for this login.");
        setZohoModalView("connect");
        setShowZohoModal(true);
      } else if (accounts.length === 1) {
        confirmZohoAccounts(accounts);
      } else {
        setZohoAccounts(accounts);
        // Default to all selected - the common case is connecting every
        // org returned, not picking just one; user can deselect.
        setSelectedZohoAccountIds(accounts.map((a) => a.id));
        setZohoModalView("confirm");
        setShowZohoModal(true);
      }
    } catch {
      setZohoModalError("Could not read the Zoho organizations returned. Please try connecting again.");
      setShowZohoModal(true);
    }
    // Only ever run once, on mount — reacting to searchParams here would
    // re-fire after the setSearchParams() call above clears them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToNextSection = useCallback(() => {
    const sectionKeys = Object.keys(sectionRefs.current);
    // if (sectionKeys.includes('taxes')) {
    //     let index = sectionKeys.indexOf('taxes');

    //     if (index !== -1) {
    //         // Remove the element from its current position
    //         let [element] = sectionKeys.splice(index, 1);

    //         // Add the element to the start of the array
    //         sectionKeys.unshift(element);
    //     }
    // }
    const nextSectionIndex = currentVisibleSectionIndex + 1; // Calculate the next section index
    const nextSectionKey = sectionKeys[nextSectionIndex]; // Get the key for the next section
    if (nextSectionKey) {
      const nextSection = sectionRefs.current[nextSectionKey].current;
      setCurrentVisibleSectionIndex(nextSectionIndex);
      nextSection?.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentVisibleSectionIndex]);

  // Determine the currently visible section based on the top 20% criteria
  const updateVisibleSectionIndex = useCallback(() => {
    const container = sectionRootContainerRef.current;
    const visibleThreshold = container.clientHeight * 0.2; // Top 20% height of the container
    let foundSectionIndex = null;

    const sectionKeys = Object.keys(sectionRefs.current);
    sectionKeys.forEach((key, index) => {
      const section = sectionRefs.current[key].current;
      // console.log('section', section, 'container', container);
      if (section && container) {
        const sectionTop = section.offsetTop - container.scrollTop;
        const sectionBottom = sectionTop + section.offsetHeight;
        // console.log('sectionTop', sectionTop, 'sectionBottom', sectionBottom, 'visibleThreshold', visibleThreshold)
        // If any part of the section is within the top 20% of the container
        if (sectionTop < visibleThreshold && sectionBottom > 0) {
          foundSectionIndex = index;
        }
      }
    });

    // console.log('foundSectionIndex', foundSectionIndex, 'currentVisibleSectionIndex', currentVisibleSectionIndex)

    // Update the state only if a different section is found
    if (
      foundSectionIndex !== null &&
      foundSectionIndex !== currentVisibleSectionIndex
    ) {
      setCurrentVisibleSectionIndex(foundSectionIndex);
    } else {
      if (foundSectionIndex == null) {
        setCurrentVisibleSectionIndex(0);
      }
    }
  }, [currentVisibleSectionIndex]);

  // Listen to scroll events on the container and update the visible section index
  useEffect(() => {
    const container = sectionRootContainerRef.current;
    container.addEventListener("scroll", updateVisibleSectionIndex, {
      passive: true,
    });

    // Initial check in case the initial scroll position is not at the top
    updateVisibleSectionIndex();

    return () => {
      container.removeEventListener("scroll", updateVisibleSectionIndex);
    };
  }, [updateVisibleSectionIndex]);

  const checkScrollButtonVisibility = useCallback(() => {
    const container = sectionRootContainerRef.current;

    const sectionKeys = Object.keys(sectionRefs.current);
    const lastSectionKey = sectionKeys[sectionKeys.length - 1];
    const lastSectionRef = sectionRefs.current[lastSectionKey];
    if (container && lastSectionRef && lastSectionRef.current) {
      const { bottom } = lastSectionRef.current.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // console.log('bottom', bottom, 'containerRect', containerRect.bottom)
      // Check if the bottom of the last section is less than or equal to the container's bottom
      const threshold = 10;

      const isLastSectionFullyVisible =
        bottom <= containerRect.bottom + threshold;
      // console.log('isLastSectionFullyVisible', isLastSectionFullyVisible)
      // Hide the scroll button if the last section is fully visible
      setShowScrollButton(!isLastSectionFullyVisible);
    }
  }, []);

  useEffect(() => {
    const container = sectionRootContainerRef.current;

    const handleScroll = () => {
      checkScrollButtonVisibility();
    };

    container.addEventListener("scroll", handleScroll);

    // Initial check
    checkScrollButtonVisibility();

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [checkScrollButtonVisibility]);

  useEffect(() => {
    if (sectionRootContainerRef.current) {
      // Scrolling to the top of the container on component mount
      sectionRootContainerRef.current.scrollTop = 0;
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      submitForm: () => {
        return new Promise((resolve) => {
          formRef.current.submit();
          // console.log("pristing", formRef?.current)
          console.log("formStepsValidity", formStepsValidity);
          if (!formStepsValidity[UnderwritingSteps.revenue]) {
            scrollToFirstError();
            // console.log('formRef?.current?.getState()', formRef?.current?.getState())
            resolve({
              success: false,
              values: formRef?.current?.getState().values,
              pristine: formRef?.current?.getState().pristine,
              newDocIds: newUploadedDocIds,
              deleteDocIds: deleteDocIds,
            });
          } else {
            deleteFileKeysOnSubmit();
            resolve({
              success: true,
              values: formRef?.current?.getState().values,
              pristine: formRef?.current?.getState().pristine,
              newDocIds: newUploadedDocIds,
              deleteDocIds: deleteDocIds,
            });
          }
        });
      },
    }),
    [formStepsValidity]
  );

  const deleteFileKeysOnSubmit = () => {
    if (deleteFileKeys && deleteFileKeys.length) {
      for (const fileKey of deleteFileKeys) {
        try {
          API.post("/app/DeleteDoc", { key: fileKey }).then((res) => res.data);
        } catch (error) {
          console.log("error", error);
        }
      }
    }
  };

  const scrollToFirstError = () => {
    try {
      const formState = formRef.current.getState();
      const { errors } = formState;
      // console.log('form state', formState);
      // console.log('formState', formState)
      const firstErrorField = findFirstErrorField(errors);
      console.log("firstErrorField", firstErrorField);
      const errorField =
        document.getElementById(firstErrorField) ??
        document.querySelector(`[name="${firstErrorField}"]`);
      console.log("errorField", errorField);
      if (errorField) {
        setTimeout(() => {
          errorField.scrollIntoView({ behavior: "smooth" });
        }, 0);
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <div className={styles.rootContainer} ref={sectionRootContainerRef}>
      <Form
        initialValues={formData}
        mutators={{
          ...arrayMutators,
          setFieldTouched: setFieldTouchedMutator,
        }}
        onSubmit={(values) => {
          return {};
        }}
        validate={(values) => {
          // console.log('values', values)
          let errors: any = {};
          const revenueDetails = values?.revenueDetails;
          // console.log("revenueDetails", revenueDetails);
          // Revenue column and validation commented out
          // if (revenueDetails && revenueDetails.length) {
          //   let isAllRevenueMissing = true;
          //   let revenueDetailsError = [];
          //   revenueDetails.forEach((revenueDetail, index) => {
          //     if (!revenueDetail?.revenue && revenueDetail?.revenue !== 0) {
          //       if (
          //         revenueDetail?.auditedFinancials?.uploadedFile ||
          //         revenueDetail?.itReturns?.uploadedFile
          //       ) {
          //         isAllRevenueMissing = false;
          //       }
          //     } else {
          //       isAllRevenueMissing = false;
          //     }
          //   });
          //   if (isAllRevenueMissing) {
          //     errors["isRevenueMissing"] = `Enter at least one FY's revenue`;
          //   }
          // }
          const { errors: revenueFormErrors } = revenueFormValidation(
            values,
            underwritingFileJobs
          );
          errors = { ...errors, ...revenueFormErrors };
          console.log("error", errors);
          return errors;
        }}
        render={({ values, handleSubmit, form }) => {
          formRef.current = form;
          return (
            <form className={styles.formWrapper} onSubmit={handleSubmit}>
              <FormSpy
                subscription={{ valid: true, pristine: true, values: true }}
                onChange={(state) => {
                  // console.log('formState', state)
                  setFormPristine(state.pristine);
                  // formStepsValidityRef.current[UnderwritingSteps.company] = !state.valid;
                  setStepValidity(UnderwritingSteps.revenue, state.valid);
                  saveToLocalStorage?.(state.values);
                }}
              />
              <div className={styles.formRootContainer}>
                {/* Tally sync section — hidden for now.
                <div className={styles.sectionContainer}>
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>Books</div>
                    <div className={styles.sectionSubTitleContainer}>
                      <div className={styles.sectionSubTitle}>
                        Connect your accounting software so we can read your
                        books directly.
                      </div>
                    </div>
                  </div>
                  {showTallyRow && (
                  <div className={styles.integrationRow}>
                    <img src={tallyLogo} alt="Tally" className={styles.integrationLogo} />
                    <div className={styles.integrationLabelCell}>Tally</div>
                    <div className={styles.integrationActionCell}>
                      {!isTallyConnected && !tallyFlow.isConnected ? (
                        <MeritoButton
                          type="primary-1"
                          size="md"
                          btnText="Connect Tally"
                          onClick={tallyFlow.open}
                        />
                      ) : tallyFlow.stage === "success" || tallySynced ? (
                        <div className={styles.integrationConnectedBadge}>Connected</div>
                      ) : (
                        <MeritoButton
                          type="outline-dim"
                          size="md"
                          icon={<MdSync aria-hidden="true" />}
                          btnText="Resync Tally"
                          className={styles.integrationResyncButton}
                          onClick={tallyFlow.openInstall}
                        />
                      )}
                    </div>
                  </div>
                  )}
                  {showZohoRow && (
                  <div className={styles.integrationRow}>
                    <img src={zohoLogo} alt="Zoho Books" className={styles.integrationLogo} />
                    <div className={styles.integrationLabelCell}>
                      <div>Zoho Books</div>
                      {zohoStatus !== "not_connected" && zohoStatus !== "checking" && (
                        <div className={styles.integrationSubLabel}>
                          Connected
                          {zohoLastSyncedAt &&
                            ` · Last sync: ${moment(zohoLastSyncedAt).format("ddd D MMM")}`}
                        </div>
                      )}
                    </div>
                    <div className={styles.integrationActionCell}>
                      {zohoStatus === "not_connected" || zohoStatus === "checking" ? (
                        <MeritoButton
                          type="primary-1"
                          size="md"
                          btnText="Connect Zoho Books"
                          onClick={() => {
                            setZohoModalError("");
                            setZohoModalView("connect");
                            setShowZohoModal(true);
                          }}
                        />
                      ) : (
                        <MeritoButton
                          type="outline-dim"
                          size="md"
                          icon={<MdSync aria-hidden="true" />}
                          btnText={isZohoResyncing ? "Resyncing…" : "Resync Data"}
                          loading={isZohoResyncing}
                          showLoadingWithText
                          disabled={isZohoResyncing}
                          className={styles.integrationResyncButton}
                          onClick={handleZohoResync}
                        />
                      )}
                    </div>
                  </div>
                  )}
                </div>
                {tallyFlow.isOpen && (
                  <TallyDashboardSyncModal
                    stage={tallyFlow.stage}
                    failed={tallyFlow.failed}
                    receivingStartedAt={tallyFlow.receivingStartedAt}
                    onClose={tallyFlow.close}
                    onContinue={tallyFlow.continueToInstall}
                    onBack={tallyFlow.back}
                    onDownload={tallyFlow.downloadTdl}
                  />
                )}
                <ModalDialog
                  visible={showZohoModal}
                  toggle={() => setShowZohoModal(false)}
                  size="md"
                  title={
                    zohoModalView === "connect"
                      ? "Set Up Automatic Sync With Zoho"
                      : "Choose your Zoho organization"
                  }
                >
                  {zohoModalView === "connect" ? (
                    <div className={styles.zohoModalBody}>
                      <div className={styles.zohoModalDescription}>
                        Fetches your bills from Zoho. Read-only access.
                      </div>
                      {zohoModalError && (
                        <div className={styles.zohoModalError}>{zohoModalError}</div>
                      )}
                      <MeritoButton
                        type="primary-1"
                        size="md2"
                        btnText={isZohoConnecting ? "Connecting…" : "Connect Zoho Books"}
                        loading={isZohoConnecting}
                        showLoadingWithText
                        disabled={isZohoConnecting}
                        onClick={connectZoho}
                      />
                      <div className={styles.zohoModalHelper}>
                        You'll be redirected to Zoho to log in.
                      </div>
                    </div>
                  ) : (
                    <div className={styles.zohoModalBody}>
                      {zohoModalError && (
                        <div className={styles.zohoModalError}>{zohoModalError}</div>
                      )}
                      <div className={styles.zohoModalOrgList}>
                        {zohoAccounts.map((account) => {
                          const isSelected = selectedZohoAccountIds.includes(account.id);
                          return (
                            <div
                              key={account.id}
                              className={`${styles.zohoModalOrgItem} ${
                                isSelected ? styles.zohoModalOrgItemSelected : ""
                              }`}
                              onClick={() =>
                                setSelectedZohoAccountIds((prev) =>
                                  isSelected
                                    ? prev.filter((id) => id !== account.id)
                                    : [...prev, account.id]
                                )
                              }
                            >
                              {isSelected ? (
                                <AiFillCheckSquare className={styles.zohoModalOrgCheckbox} />
                              ) : (
                                <MdCheckBoxOutlineBlank className={styles.zohoModalOrgCheckbox} />
                              )}
                              {account.name}
                            </div>
                          );
                        })}
                      </div>
                      <MeritoButton
                        type="primary-1"
                        size="md2"
                        btnText={isConfirmingZoho ? "Connecting…" : "Continue"}
                        loading={isConfirmingZoho}
                        showLoadingWithText
                        disabled={selectedZohoAccountIds.length === 0 || isConfirmingZoho}
                        onClick={() => {
                          const accounts = zohoAccounts.filter((a) =>
                            selectedZohoAccountIds.includes(a.id)
                          );
                          if (accounts.length) confirmZohoAccounts(accounts);
                        }}
                      />
                    </div>
                  )}
                </ModalDialog>
                */}
                <div
                  ref={sectionRefs?.current?.revenueDetails as any}
                  className={styles.sectionContainer}
                >
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>Financials</div>
                    <div className={styles.sectionSubTitleContainer}>
                      <div className={styles.sectionSubTitle}>
                        Upload your financials for each year. Combined or
                        separate documents accepted.
                      </div>
                    </div>
                  </div>
                  <div className={styles.sectionFieldsContainer}>
                    <FieldArray name="revenueDetails">
                      {({ fields, meta: { error, touched } }) => {
                        return (
                          <>
                            <div className={styles.revenueCardList}>
                              {fields.map((item, index) => {
                                return (
                                  <RevenueRow
                                    key={item}
                                    fieldIndex={index}
                                    fieldName={item}
                                    newUploadedDocIds={newUploadedDocIds}
                                    setNewUploadedDocIds={setNewUploadedDocIds}
                                    deleteDocIds={deleteDocIds}
                                    setDeleteDocIds={setDeleteDocIds}
                                  />
                                );
                              })}
                            </div>
                          </>
                        );
                      }}
                    </FieldArray>
                    {/* <RevenueError /> */}
                  </div>
                </div>

                <div
                  id="misReports"
                  ref={sectionRefs?.current?.misReports as any}
                  className={styles.sectionContainer}
                >
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>MIS Reports</div>
                    <div className={styles.sectionSubTitleContainer}>
                      <div className={styles.sectionSubTitle}>
                        Your monthly management reports for the last 12 months.
                      </div>
                    </div>
                  </div>
                  <div className={styles.sectionFieldsContainer}>
                    <div className={styles.revenueCardList}>
                      <MisReportRow
                        newUploadedDocIds={newUploadedDocIds}
                        setNewUploadedDocIds={setNewUploadedDocIds}
                        deleteDocIds={deleteDocIds}
                        setDeleteDocIds={setDeleteDocIds}
                      />
                    </div>
                  </div>
                </div>

                <div
                  id="debtSchedule"
                  className={styles.sectionContainer}
                  ref={sectionRefs?.current?.debtSchedule as any}
                >
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>Debt Schedule</div>
                  </div>
                  <DebtSchedule />
                </div>

                {/* <div
                  id="debtorSchedule"
                  className={styles.sectionContainer}
                  ref={sectionRefs?.current?.debtorSchedule as any}
                >
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>Debtor Schedule</div>
                  </div>
                  <DebtorSchedule />
                </div>

                <div
                  className={styles.sectionContainer}
                  ref={sectionRefs?.current?.unsoldInventory as any}
                >
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionTitle}>
                      Inventory Position
                    </div>
                  </div>
                  <div className={styles.filedsContainer}>
                    <Field
                      name="unsoldInventory.underwriting.value"
                      validate={(value) => {
                        if (!value) {
                          return "Enter the value";
                        }
                        return "";
                      }}
                    >
                      {(props) => {
                        return (
                          <div className={styles.fieldContainer}>
                            <OutlinedFormInput
                              {...props.input}
                              textFieldType="outlined"
                              fieldType="lg"
                              showBorder={false}
                              placeholder={"10,00,000"}
                              showPlaceholder={false}
                              inpuType="number"
                              label={`Value of Unsold Inventory as on ${moment()
                                .subtract(1, "month")
                                .endOf("month")
                                .format("MMM DD, YYYY")}`}
                              // containerStyles={{ padding: "0" }}
                              name={props.input.name}
                              value={props.input.value}
                              onChange={(e) => {
                                const numericValue = parseNumberForDB(
                                  e.target.value,
                                  false
                                );
                                console.log("numericValue", numericValue);
                                props.input.onChange(numericValue);
                              }}
                              onBlur={(e) => {
                                const value = props.input.value;
                                const formattedValue = onBlurFormatNumber(
                                  value,
                                  0
                                );
                                props.input.onChange(formattedValue);
                                // console.log('inputValue', value)
                                props.input.onBlur(e);
                              }}
                              unitPlaceHolder="₹"
                              error={props.meta.error}
                              touched={props.meta.touched}
                              isCurrency={true}
                              maxLength={10}
                            />

                            <div className={styles.subText}>
                              Use cost or book value, not MRP.
                            </div>
                          </div>
                        );
                      }}
                    </Field>
                  </div>
                </div> */}
              </div>
            </form>
          );
        }}
      />

      <div className={styles.scrollBtnContainer}>
        {showScrollButton && (
          <MeritoButton
            size="md2"
            onClick={scrollToNextSection}
            isIconToRight={true}
            btnText="Scroll to Next Section"
            type="secondary-1"
            icon={<DownArrowIcon />}
          />
        )}
      </div>
    </div>
  );
});

export default RevenueDetails;
